/**
 * ТЗ 0077 — SMS-напоминания по договору. Словари и чистые помощники.
 *
 * Статусы, причины и подписи шагов приходят строчными строками (бэкенд
 * намеренно не делает из них graphene-enum): сравнивать только с константами
 * отсюда. Каждый словарь — это и список ключей `esign.outreach.*`, который
 * проверяет тест паритета локалей: новый код без перевода уронит тест, а не
 * покажет сырой ключ.
 *
 * Модуль без React, Apollo и i18n: подписи — ключи, их переводит вызывающий.
 */

/** Почему канал для договора недоступен (`ContractOutreachType.reason`). */
export const OUTREACH_REASONS = [
  'disabled_on_server',
  'disabled',
  'entity_not_allowed',
  'no_phone',
  'not_allowlisted',
  'foreign_number',
  'opted_out',
  'closed',
  // Перепроверка 0084: у следующего шага нет включённого шаблона текста —
  // карточка не обещает SMS, которое не уйдёт.
  'no_template',
] as const;

export const OUTREACH_STATUSES = [
  'pending',
  'sending',
  'sent',
  'failed',
  'skipped',
] as const;

/** `OutreachMessageType.skipReason` — машинные коды `outreach/constants.py`. */
export const OUTREACH_SKIP_REASONS = [
  'disabled_on_server',
  'disabled',
  'entity_not_allowed',
  'closed',
  'stale',
  'opted_out',
  'daily_cap',
  'no_phone',
  'invalid_phone',
  'superseded',
  'stopped_manually',
  'too_long',
  'links_not_allowed',
  'foreign_number',
  'not_allowlisted',
  // Перепроверка 2026-09-22: эти коды приходили в лестницу и показывались
  // сырыми — очередь снимает строку и при отмене, и при общем стопе, и
  // когда номер занят другой отправкой, и когда текста нет.
  'cancelled',
  'cancelled_by_stop',
  'busy',
  'required',
  // ТЗ 0084: текст шага — только шаблон ERP (нет включённого — SMS не уходит);
  // сократитель не настроен — длинной ссылкой SMS не уходит.
  'no_template',
  'shortlink_disabled',
] as const;

/**
 * Подписи шагов, которые бэкенд называет по имени. `nudge_N` с другим N
 * (лестницу могут удлинить в админке) показывается общим «Напоминание».
 */
export const OUTREACH_STEP_LABELS = [
  'initial',
  'nudge_1',
  'nudge_2',
  'last_call',
  'manual',
  'other',
] as const;

/** Коды `customErrors` мутаций напоминаний. */
export const OUTREACH_ERROR_CODES = [
  'NOT_FOUND',
  'SMS_DISABLED',
  'NO_PHONE',
  'WRONG_STATUS',
  'COOLDOWN',
  'OPTED_OUT',
  'STOPPED',
  'NOTHING_TO_STOP',
  'NOT_STOPPED',
  'DAILY_CAP',
  'FOREIGN_NUMBER',
  // ТЗ 0084: «Напомнить сейчас» без своего шаблона; сократитель не настроен.
  'NO_TEMPLATE',
  'SHORTLINK_DISABLED',
] as const;

/**
 * Финальные статусы доставки SMSAPI, кроме «доставлено»: их показываем
 * вместо времени доставки. `SENT` — промежуточный («у оператора»), это не
 * новость, строка о нём молчит.
 */
export const OUTREACH_UNDELIVERED_CODES = [
  'UNDELIVERED',
  'FAILED',
  'REJECTED',
  'EXPIRED',
  'NOT_FOUND',
  'STOP',
] as const;

/** Зеркало общего `TagTone` из `_detailShared/detail.styled`. */
type OutreachTagTone = 'ok' | 'warn' | 'danger' | 'neutral' | 'info' | 'gold';

/** Частый опрос — пока свежее SMS в очереди: исход обычно за секунды. */
export const OUTREACH_FAST_POLL_MS = 5000;
/** Дальше — редко: строку держат ретраи или обход, это минуты и часы. */
export const OUTREACH_SLOW_POLL_MS = 60000;
/** Сколько после постановки в очередь опрашивать часто. */
export const OUTREACH_FAST_WINDOW_MS = 10 * 60 * 1000;

export interface IOutreachMessageLike {
  status: string;
  stepLabel: string;
  skipReason?: string | null;
  scheduledFor?: string | null;
  createdAt: string;
  sentAt?: string | null;
  deliveredAt?: string | null;
  dlrStatus?: string | null;
}

const isKnown = (list: readonly string[], value: string | null | undefined) =>
  Boolean(value) && list.includes(value as string);

/** Ключ подписи шага: известный — свой, прочий `nudge_N` — общий. */
export const outreachStepKey = (stepLabel: string): string =>
  `esign.outreach.steps.${
    isKnown(OUTREACH_STEP_LABELS, stepLabel) ? stepLabel : 'other'
  }`;

/**
 * Тон плашки сообщения. Отправленное, но ещё не подтверждённое оператором —
 * «инфо», а не «ок»: зелёный обещал бы, что SMS дошло.
 */
export const outreachStatusTone = (
  message: Pick<IOutreachMessageLike, 'status' | 'deliveredAt' | 'dlrStatus'>,
): OutreachTagTone => {
  switch (message.status) {
    case 'sent':
      if (message.deliveredAt) return 'ok';
      return isKnown(OUTREACH_UNDELIVERED_CODES, message.dlrStatus)
        ? 'danger'
        : 'info';
    case 'failed':
      return 'danger';
    case 'pending':
    case 'sending':
      return 'warn';
    default:
      return 'neutral';
  }
};

/**
 * Время строки: когда ушло, иначе когда запланировано (тихие часы), иначе
 * когда поставлено в очередь.
 */
export const outreachMessageWhen = (message: IOutreachMessageLike): string =>
  message.sentAt || message.scheduledFor || message.createdAt;

export type OutreachDelivery =
  | { kind: 'delivered'; at: string }
  | { kind: 'undelivered'; code: string }
  | null;

/** Что сказать о доставке: время, финальный отказ оператора или ничего. */
export const outreachDelivery = (
  message: Pick<IOutreachMessageLike, 'deliveredAt' | 'dlrStatus'>,
): OutreachDelivery => {
  if (message.deliveredAt) {
    return { kind: 'delivered', at: message.deliveredAt };
  }
  // SENT — «у оператора», DELIVERED без времени — не новость: молчим.
  if (message.dlrStatus && !['SENT', 'DELIVERED'].includes(message.dlrStatus)) {
    return { kind: 'undelivered', code: message.dlrStatus };
  }
  return null;
};

/** Ключ статуса доставки; неизвестный код покажется как есть (`defaultValue`). */
export const outreachDeliveryKey = (code: string): string =>
  `esign.outreach.dlr.${code}`;

/**
 * Есть ли сообщение, чей исход увидит только опрос: уже отправляется или
 * ждёт в очереди без переноса (перенос на утро из-за тихих часов — это
 * часы ожидания, опрашивать их нечего).
 */
export const isOutreachInFlight = (
  messages: ReadonlyArray<
    Pick<IOutreachMessageLike, 'status' | 'scheduledFor'>
  >,
  now: number = Date.now(),
): boolean =>
  messages.some(
    (message) =>
      message.status === 'sending' ||
      (message.status === 'pending' &&
        (!message.scheduledFor ||
          new Date(message.scheduledFor).getTime() <= now)),
  );

/**
 * Интервал опроса: 0 — не опрашивать; часто — первые 10 минут после того,
 * как сообщение стало «в полёте»; дальше — раз в минуту. Без предела ящик
 * опрашивал бы сервер раз в 5 секунд часами, пока строку держат ретраи.
 */
export const outreachPollInterval = (
  messages: ReadonlyArray<
    Pick<IOutreachMessageLike, 'status' | 'scheduledFor' | 'createdAt'>
  >,
  now: number = Date.now(),
): number => {
  const inFlight = messages.filter((message) =>
    isOutreachInFlight([message], now),
  );
  if (inFlight.length === 0) return 0;
  const fresh = inFlight.some((message) => {
    const since = new Date(message.scheduledFor || message.createdAt).getTime();
    return now - since < OUTREACH_FAST_WINDOW_MS;
  });
  return fresh ? OUTREACH_FAST_POLL_MS : OUTREACH_SLOW_POLL_MS;
};

/**
 * Когда закончится частый опрос: окно `OUTREACH_FAST_WINDOW_MS` от момента,
 * когда самое свежее сообщение стало «в полёте». Интервал считается при
 * отрисовке, а опрос, не меняющий данных, ящик не перерисовывает — без
 * этого срока частый опрос не замедлялся бы никогда.
 */
export const outreachFastUntil = (
  messages: ReadonlyArray<
    Pick<IOutreachMessageLike, 'status' | 'scheduledFor' | 'createdAt'>
  >,
  now: number = Date.now(),
): number | null => {
  const ends = messages
    .filter((message) => isOutreachInFlight([message], now))
    .map(
      (message) =>
        new Date(message.scheduledFor || message.createdAt).getTime() +
        OUTREACH_FAST_WINDOW_MS,
    )
    .filter((end) => end > now);
  return ends.length ? Math.max(...ends) : null;
};

/**
 * Ближайший перенос в будущем (тихие часы, суточный лимит): в этот момент
 * ящик перечитает состояние сам — иначе «в очереди» висело бы до
 * перезагрузки, ведь сам по себе ход времени ничего не перерисовывает.
 */
export const outreachNextWakeAt = (
  messages: ReadonlyArray<
    Pick<IOutreachMessageLike, 'status' | 'scheduledFor'>
  >,
  now: number = Date.now(),
): number | null => {
  const times = messages
    .filter((message) => message.status === 'pending' && message.scheduledFor)
    .map((message) => new Date(message.scheduledFor as string).getTime())
    .filter((time) => time > now);
  return times.length ? Math.min(...times) : null;
};
